import React from "react";
import {useMediaQuery} from "@mui/material";

import {COLORS} from "../../../../../config/constants";
import ContainedButton from "../../components/buttons/Contained";
import OutlinedButton from "../../components/buttons/Outlined";
import H1 from "../../components/typography/H1";
import H4 from "../../components/typography/H4";
import LI1 from "../../components/typography/LI1";
import {typografHeadingNode} from "../../utils/typograf";
import styles from "./HowItWorksSection.module.scss";
import type {PartnerVariant} from '../../types';
import certCentersRu from '../../locales/certCentersRu';

type HowItWorksStep = {
    title: string;
    emoji: string;
    description: React.ReactNode;
};

type HowItWorksSectionProps = {
    callToActionText: string;
    variant?: PartnerVariant;
};

const steps: HowItWorksStep[] = [
    {
        title: "The client calculates the full cost",
        emoji: "🧮",
        description: <>The importer uses our calculator to work out the total import cost and select any <span className={styles.servicePhrase}>additional services</span></>,
    },
    {
        title: "The system creates a structured request",
        emoji: "📦",
        description: "Cargo details, route information and required documents are collected and checked for completeness",
    },
    {
        title: "You receive a verified request",
        emoji: "📑",
        description: "You receive a detailed request with specifications and company details attached, instead of a call just to ask about prices",
    },
    {
        title: "Your rates help shape the final decision",
        emoji: "🧩",
        description: "Your routes and rates appear on the platform at the key decision stage, when clients review the total shipment cost and choose their service providers",
    },
    {
        title: "You control the flow",
        emoji: "👀",
        description: "Choose to receive requests only for the routes, cargo types and volumes you serve",
    },
];

function HowItWorksSection({callToActionText, variant = 'transport'}: HowItWorksSectionProps) {
    const pageSteps = variant === 'cert-centers' ? certCentersRu.steps : steps;

    const isMobile = useMediaQuery('(max-width: 767px)');
    const isSmall = useMediaQuery('(min-width: 576px) and (max-width: 767px)');
    const isMedium = useMediaQuery('(min-width: 768px)');
    const CallToActionHeading = H4;
    const StepHeading = H4;

    return (
        <section className={styles.section}>
            <div className={styles.sectionContainer}>
                <div className={styles.title}>
                    <H1 sx={{
                        color: COLORS.WHITE,
                    }}>How it works</H1>
                </div>

                <div className={styles.steps}>
                    {pageSteps.map((step, index) => (
                        <article className={styles.step} key={step.title}>
                            <StepHeading sx={{
                                color: COLORS.WHITE,
                                display: 'flex',
                                '@media (min-width: 1200px)': {
                                    display: 'block',
                                    margin: 0,
                                },
                                '@media (min-width: 1400px)': {
                                    display: 'block',
                                    fontSize: '32px',
                                    lineHeight: 1.28,
                                    position: 'relative',
                                    top: '4px',
                                    alignSelf: 'center',
                                    margin: 0,
                                },
                                gap: 1,
                                margin: '0 0 0 8px',
                                '@media screen and (max-width: 767px)': {
                                    display: 'block',
                                    margin: 0,
                                },
                                '@media screen and (min-width: 576px) and (max-width: 767px)': {
                                    position: 'relative',
                                    top: '4px',
                                },
                                '@media screen and (min-width: 768px) and (max-width: 1199px)': {
                                    display: 'block',
                                    fontSize: '28px',
                                    lineHeight: 1.28,
                                    margin: 0,
                                },
                            }}>
                                <span className={styles.stepNumber}>{index + 1}. </span>
                                <span className={styles.stepTitle}>{isSmall || isMedium ? typografHeadingNode(step.title) : step.title}</span>
                            </StepHeading>
                            <div className={styles.descriptionCard}>
                                <span className={styles.emoji} aria-hidden="true">{step.emoji}</span>
                                <LI1 className={styles.descriptionText} sx={{
                                    color: COLORS.BLACK,
                                    '@media (min-width: 992px) and (max-width: 1199px)': {
                                        maxWidth: index === 1 ? '352px' : undefined,
                                    },
                                    '@media screen and (min-width: 1200px)': {
                                        fontSize: '18px',
                                        lineHeight: 1.6,
                                    },
                                }}>{step.description}</LI1>
                            </div>
                        </article>
                    ))}
                </div>

                <div className={styles.callToAction}>
                    <div className={styles.callToActionContent}>
                        <span className={styles.flag} aria-hidden="true">🏁</span>
                        <CallToActionHeading sx={{
                            color: COLORS.WHITE,
                            '@media (min-width: 1200px)': {
                                position: 'relative',
                                top: '8px',
                            },
                        }}>{callToActionText}</CallToActionHeading>
                    </div>
                    <div className={styles.buttons}>
                        <ContainedButton className={styles.actionButton} isWhite icon={isMobile ? null : 'handshake'} comfortableLabel>Become a partner</ContainedButton>
                        <OutlinedButton className={styles.actionButton} isBlack icon={isMobile ? null : 'play'} comfortableLabel solidTabletBorder>Watch demo</OutlinedButton>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HowItWorksSection;
